import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { SunriseIcon, FlaskConicalIcon, EyeIcon, TreesIcon, ZapIcon, SkullIcon, SproutIcon, MapIcon, PuzzleIcon, RotateCcwIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.07
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.97
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
interface ChapterCardProps {
  number: number;
  title: string;
  icon: React.ReactNode;
  accentColor: string;
  chapterLabel: string;
}
function ChapterCard({
  number,
  title,
  icon,
  accentColor,
  chapterLabel
}: ChapterCardProps) {
  return <motion.div variants={cardVariants} className="group relative" data-id="element-67">
      <div className="relative rounded-xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] p-6 h-full overflow-hidden transition-colors duration-500 hover:bg-white/[0.04]" data-id="element-68">
        {/* Glow line at top */}
        <div className="absolute top-0 left-4 right-4 h-px opacity-40" style={{
        background: `linear-gradient(to right, transparent, ${accentColor}, transparent)`
      }} data-id="element-69" />

        <div className="flex items-start gap-4" data-id="element-70">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
          backgroundColor: `${accentColor}15`
        }} data-id="element-71">
            {icon}
          </div>
          <div data-id="element-72">
            <p className="font-sans text-xs text-white/20 tracking-widest uppercase mb-1" data-id="element-73">
              {chapterLabel} {number}
            </p>
            <h3 className="font-serif text-lg font-bold text-white" data-id="element-74">{title}</h3>
          </div>
        </div>
      </div>
    </motion.div>;
}
export function ChaptersSection() {
  const {
    t
  } = useLanguage();
  const chapters = [{
    number: 1,
    titleKey: 'chapters.1' as const,
    icon: <SunriseIcon className="w-5 h-5 text-gold" data-id="element-75" />,
    accentColor: '#d4a853'
  }, {
    number: 2,
    titleKey: 'chapters.2' as const,
    icon: <FlaskConicalIcon className="w-5 h-5 text-purple-electric" data-id="element-76" />,
    accentColor: '#8b5cf6'
  }, {
    number: 3,
    titleKey: 'chapters.3' as const,
    icon: <EyeIcon className="w-5 h-5 text-astral" data-id="element-77" />,
    accentColor: '#3b82f6'
  }, {
    number: 4,
    titleKey: 'chapters.4' as const,
    icon: <TreesIcon className="w-5 h-5 text-green-400" data-id="element-78" />,
    accentColor: '#4ade80'
  }, {
    number: 5,
    titleKey: 'chapters.5' as const,
    icon: <ZapIcon className="w-5 h-5 text-astral" data-id="element-79" />,
    accentColor: '#3b82f6'
  }, {
    number: 6,
    titleKey: 'chapters.6' as const,
    icon: <SkullIcon className="w-5 h-5 text-purple-electric" data-id="element-80" />,
    accentColor: '#8b5cf6'
  }, {
    number: 7,
    titleKey: 'chapters.7' as const,
    icon: <SproutIcon className="w-5 h-5 text-green-400" data-id="element-81" />,
    accentColor: '#4ade80'
  }, {
    number: 8,
    titleKey: 'chapters.8' as const,
    icon: <MapIcon className="w-5 h-5 text-gold" data-id="element-82" />,
    accentColor: '#d4a853'
  }, {
    number: 9,
    titleKey: 'chapters.9' as const,
    icon: <PuzzleIcon className="w-5 h-5 text-gold" data-id="element-83" />,
    accentColor: '#d4a853'
  }, {
    number: 10,
    titleKey: 'chapters.10' as const,
    icon: <RotateCcwIcon className="w-5 h-5 text-purple-electric" data-id="element-84" />,
    accentColor: '#8b5cf6'
  }];
  return <section className="relative w-full py-8 sm:py-14" data-id="element-85">
      {/* Background glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-purple-electric/[0.03] rounded-full blur-[150px] pointer-events-none" data-id="element-86" />

      <div className="relative max-w-6xl mx-auto px-6" data-id="element-87">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center mb-6 sm:mb-10" data-id="element-88">
          <p className="font-sans text-sm text-gold tracking-[0.2em] uppercase mb-4" data-id="element-89">
            {t('chapters.eyebrow')}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white" data-id="element-90">
            {t('chapters.title')}
          </h2>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-91">
          {chapters.map(chapter => <ChapterCard key={chapter.number} number={chapter.number} title={t(chapter.titleKey)} icon={chapter.icon} accentColor={chapter.accentColor} chapterLabel={t('chapters.chapter')} data-id="element-92" />)}
        </motion.div>
      </div>
    </section>;
}